import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatRupiah } from "@/lib/midtrans";
import PaymentLogos from "@/components/PaymentLogos";
import WhatsAppFab from "@/components/WhatsAppFab";
import {
  Heart, Sparkles, Palette, Users, Send, GripVertical, Music,
  Calendar, Camera, Gift, MessageCircle, Shield, ArrowRight, Check, Crown, LogIn,
  Star, Award, Bell, Bookmark, Cake, Diamond, Flower, Zap,
} from "lucide-react";

const LOGO = "/wedifylab_logo3.png";

// Map of icon name (string from CMS) → Lucide component.
const ICON_MAP = {
  Heart, Sparkles, Palette, Users, Send, GripVertical, Music,
  Calendar, Camera, Gift, MessageCircle, Shield, Crown, Star, Check,
  Award, Bell, Bookmark, Cake, Diamond, Flower, Zap,
};

const FEATURES = [
  { icon: Palette, title: "13 Tab Kustomisasi Undangan", desc: "Cover, Pembuka, Mempelai Pria & Wanita, Countdown, Acara, Kisah Kami, Galeri, Wedding Gift, RSVP, Ucapan, Penutup — semuanya bisa diatur detail." },
  { icon: Sparkles, title: "15 Tema Undangan Premium", desc: "Wekita-style modern: script font mewah, ornamen SVG, animasi scroll halus, mode terang/gelap. Klasik hingga Royal Purple Gold." },
  { icon: GripVertical, title: "Drag-Reorder Kisah Kami", desc: "Seret bilah untuk mengatur urutan timeline cerita cinta Anda dengan bebas." },
  { icon: Users, title: "Manajemen Tamu Lengkap", desc: "Data tamu dari mempelai pria/wanita, kategori keterangan, RSVP kehadiran, jumlah rombongan — tersimpan rapi." },
  { icon: Send, title: "Blast WhatsApp + Template", desc: "Broadcast link undangan personal via WhatsApp. Template pesan bisa disimpan dengan variabel {nama}, {tanggal}, {lokasi}, {link_undangan}." },
  { icon: Calendar, title: "Rundown & Countdown", desc: "Susun rundown acara jam demi jam. Countdown mundur otomatis + tombol Simpan ke Kalender (.ics)." },
  { icon: Gift, title: "Wedding Gift Digital", desc: "Kartu rekening bank dengan tombol Salin Nomor — memudahkan tamu memberi amplop digital." },
  { icon: Camera, title: "Galeri + Lightbox", desc: "Unggah galeri foto masonry, tamu bisa klik untuk perbesar dengan lightbox modal." },
  { icon: MessageCircle, title: "Buku Tamu & Ucapan", desc: "Tamu meninggalkan ucapan & doa langsung dari halaman undangan (dengan captcha anti-spam)." },
  { icon: Shield, title: "Multi-Role & Aman", desc: "Sysadmin, Admin, Pria, Wanita — akses berbeda, kode pasangan unik, reset password aman via email." },
  { icon: Music, title: "Musik Latar", desc: "Pilih musik pengiring undangan. Toggle mute/unmute untuk tamu." },
  { icon: Heart, title: "Budget, Vendor, Seserahan", desc: "Kelola anggaran, bandingkan vendor, catat seserahan hantaran — semua di satu tempat." },
];

const BENEFITS = [
  { title: "Hemat Waktu", desc: "Semua kebutuhan pernikahan digital di satu aplikasi. Tidak perlu jasa desain terpisah." },
  { title: "Personal & Unik", desc: "13 tab custom + 15 tema premium — undangan Anda tidak akan sama dengan orang lain." },
  { title: "Kolaborasi Pasangan", desc: "Login terpisah untuk Pria & Wanita — bekerja bersama tanpa saling tumpang tindih." },
  { title: "Selalu Terbaru", desc: "Update fitur berkala, tema baru, & prioritas dukungan tanpa biaya tambahan." },
];

const PLANS = [
  {
    id: "bulan1", name: "Paket 1 Bulan", period: "/bulan",
    features: [
      "Akses semua fitur",
      "Template undangan standar",
      "Manajemen tamu hingga 200 orang",
      "Blast WhatsApp + 3 template",
      "Musik latar & galeri hingga 8 foto",
      "Buku tamu & RSVP",
      "Berlaku 30 hari",
    ],
  },
  {
    id: "bulan6", name: "Paket 6 Bulan", period: "/6 bulan",
    highlight: true, badge: "Paling Populer",
    features: [
      "Semua fitur di Paket 1 Bulan",
      "Template standar",
      "Manajemen tamu unlimited",
      "Blast WhatsApp + template unlimited",
      "Galeri hingga 20 foto",
      "Drag-reorder Kisah Kami",
      "Rundown & countdown lengkap",
      "Berlaku 180 hari",
    ],
  },
  {
    id: "premium", name: "Paket Premium", period: "/lifetime",
    badge: "Full Akses",
    features: [
      "Semua fitur, tanpa batasan",
      "15 tema premium (Sekar, Kirana, Bintang Malam, dll)",
      "Tamu unlimited",
      "Galeri unlimited",
      "Prioritas fitur baru",
      "Dukungan langsung",
      "Akses selamanya",
    ],
  },
];

function Reveal({ children, delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const [prices, setPrices] = useState({ bulan1: 99000, bulan6: 399000, premium: 799000 });
  const [plansMeta, setPlansMeta] = useState({});
  const [popularPlan, setPopularPlan] = useState("bulan6");
  const [testimonials, setTestimonials] = useState([]);
  const [cms, setCms] = useState({});
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/plan-prices`)
      .then((r) => { setPrices(r.data.prices); setPlansMeta(r.data.plans || {}); setPopularPlan(r.data.popular_plan || "bulan6"); }).catch(() => {});
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/testimonials`)
      .then((r) => { setTestimonials(r.data?.testimonials || []); }).catch(() => {});
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/cms`)
      .then((r) => { setCms(r.data?.content || {}); }).catch(() => {});
  }, []);

  // ---- CMS resolvers with strict fallback to hardcoded defaults ----
  const heroCms = cms.hero || {};
  const featCms = cms.features || {};
  const priceCms = cms.pricing || {};
  const footerCms = cms.footer || {};

  // Feature items: use CMS items when at least one; else fallback to FEATURES.
  const featureItems = (Array.isArray(featCms.items) && featCms.items.length > 0)
    ? featCms.items.map((f) => ({
        icon: ICON_MAP[f.icon] || Sparkles,
        title: f.title || "",
        desc: f.desc || "",
      }))
    : FEATURES;

  // Merge plan descriptions from CMS into default PLANS array.
  const cmsPlans = priceCms.plans || {};
  const plansMerged = PLANS.map((p) => {
    const cp = cmsPlans[p.id] || {};
    const feats = Array.isArray(cp.features) && cp.features.length > 0
      ? cp.features.filter((x) => (x || "").trim()) : p.features;
    return {
      ...p,
      short_desc: (cp.short_desc || "").trim(),
      features: feats.length > 0 ? feats : p.features,
    };
  });
  return (
    <div className="min-h-screen bg-[#0f0a08] text-neutral-100" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="landing-page">
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,400&family=Inter:wght@400;500;600;700&display=swap" />

      {/* HEADER */}
      <header className="sticky top-0 z-50 border-b border-[#c9a35c22] bg-[#0f0a08]/90 backdrop-blur-lg" data-testid="landing-header">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
            <img src={LOGO} alt="Wedifylab" className="h-11 w-11 object-contain" />
            <div className="leading-tight">
              <p className="font-heading text-xl font-semibold tracking-wide text-[#e6c98a]" style={{ fontFamily: "'Inter', sans-serif" }}>Wedifylab</p>
              <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-400" style={{ fontFamily: "'Inter', sans-serif" }}>Wedding Planner &amp; Digital Invitation</p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <a href="#paket" className="hidden rounded-full px-4 py-2 text-sm text-neutral-300 transition-colors hover:text-[#e6c98a] sm:inline-block" style={{ fontFamily: "'Inter', sans-serif" }}>Paket</a>
            <a href="#fitur" className="hidden rounded-full px-4 py-2 text-sm text-neutral-300 transition-colors hover:text-[#e6c98a] sm:inline-block" style={{ fontFamily: "'Inter', sans-serif" }}>Fitur</a>
            <Button onClick={() => nav("/login")} data-testid="landing-login-btn"
              className="gap-2 rounded-full px-5 font-semibold text-[#0f0a08] transition-transform hover:-translate-y-0.5"
              style={{ background: "linear-gradient(90deg,#c9a35c,#e6c98a)", fontFamily: "'Inter', sans-serif" }}>
              <LogIn className="h-4 w-4" /> Masuk
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden px-5 py-20 sm:py-28" data-testid="landing-hero">
        <div className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "radial-gradient(60% 50% at 50% 20%, #c9a35c33 0%, transparent 70%)" }} />
        <div className="pointer-events-none absolute -top-10 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full opacity-20 blur-3xl" style={{ background: "#c9a35c" }} />
        <div className="relative mx-auto max-w-4xl text-center">
          <Reveal>
            <Badge className="rounded-full border-[#c9a35c66] bg-[#c9a35c11] px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] text-[#e6c98a]" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="landing-hero-badge">
              <Crown className="mr-1.5 h-3 w-3" /> {heroCms.badge || "Wedding Planner Terlengkap"}
            </Badge>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 text-4xl font-semibold leading-tight tracking-tight sm:text-6xl md:text-7xl" style={{ fontFamily: "'Inter', sans-serif", color: "#f7efe4" }} data-testid="landing-hero-title">
              {heroCms.title ? (
                <>{heroCms.title.split("\n").map((ln, i, arr) => (
                  <React.Fragment key={i}>{ln}{i < arr.length - 1 && <br />}</React.Fragment>
                ))}{heroCms.title_highlight && (
                  <> <span className="italic text-[#e6c98a]" style={{ fontFamily: "'Great Vibes', cursive" }}>{heroCms.title_highlight}</span></>
                )}</>
              ) : (
                <>Rencanakan Pernikahan<br />Impian Anda dengan <span className="italic text-[#e6c98a]" style={{ fontFamily: "'Great Vibes', cursive" }}>Cinta</span></>
              )}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-neutral-300 sm:text-lg" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="landing-hero-subtitle">
              {heroCms.subtitle ? heroCms.subtitle : (
                <><b className="text-[#e6c98a]">Wedifylab</b> adalah platform untuk <b>wedding planner</b> dan <b>undangan digital pernikahan</b>. Kelola budget, vendor, tamu, rundown, hingga seserahan — plus undangan digital elegan yang siap dibagikan.</>
              )}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 grid grid-cols-3 gap-3 rounded-2xl border border-[#c9a35c33] bg-[#1a120e]/60 p-4 backdrop-blur-md sm:mt-10">
              <div className="text-center">
                <p className="font-serif text-3xl text-[#e6c98a]" style={{ fontFamily: "'Inter', sans-serif" }}>14+</p>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400" style={{ fontFamily: "'Inter', sans-serif" }}>Menu Manajemen</p>
              </div>
              <div className="text-center">
                <p className="font-serif text-3xl text-[#e6c98a]" style={{ fontFamily: "'Inter', sans-serif" }}>15</p>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400" style={{ fontFamily: "'Inter', sans-serif" }}>Tema Undangan</p>
              </div>
              <div className="text-center">
                <p className="font-serif text-3xl text-[#e6c98a]" style={{ fontFamily: "'Inter', sans-serif" }}>∞</p>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400" style={{ fontFamily: "'Inter', sans-serif" }}>Kolaborasi Pasangan</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Button onClick={() => nav("/login")} size="lg" data-testid="landing-hero-cta"
                className="gap-2 rounded-full px-8 py-6 text-base font-semibold text-[#0f0a08] transition-transform hover:-translate-y-1"
                style={{ background: "linear-gradient(90deg,#c9a35c,#e6c98a)", fontFamily: "'Inter', sans-serif" }}>
                {heroCms.cta_primary || "Mulai Rencanakan"} <ArrowRight className="h-4 w-4" />
              </Button>
              <a href="#paket">
                <Button variant="outline" size="lg" data-testid="landing-hero-plans" className="gap-2 rounded-full border-[#c9a35c66] px-8 py-6 text-base text-[#e6c98a] transition-transform hover:-translate-y-1 hover:bg-[#c9a35c11]" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {heroCms.cta_secondary || "Lihat Paket"}
                </Button>
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.5}>
            <div className="mt-14 inline-flex items-center gap-2 rounded-full border border-[#c9a35c22] bg-[#c9a35c08] px-5 py-2 text-xs text-neutral-400" style={{ fontFamily: "'Inter', sans-serif" }}>
              <Sparkles className="h-3.5 w-3.5 text-[#e6c98a]" />
              <span>Termasuk: <b className="italic text-[#e6c98a]" style={{ fontFamily: "'Great Vibes', cursive" }}>Undangan Digital Sederhana</b> untuk hari besar Anda</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FEATURES */}
      <section id="fitur" className="px-5 py-24" data-testid="landing-features">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <p className="text-center text-xs uppercase tracking-[0.4em] text-[#c9a35c]" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="landing-features-eyebrow">{featCms.eyebrow || "Fitur Wedding Planner"}</p>
            <h2 className="mt-3 text-center text-4xl italic sm:text-5xl" style={{ fontFamily: "'Great Vibes', cursive", color: "#f7efe4" }} data-testid="landing-features-heading">{featCms.heading || "Rencanakan pernikahan Anda dengan mudah"}</h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-neutral-400" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="landing-features-desc">
              {featCms.description || "Dari budget hingga tamu, dari vendor hingga rundown — plus bonus undangan digital elegan sebagai pelengkap."}
            </p>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {featureItems.map((f, i) => {
              const IconC = f.icon || Sparkles;
              return (
              <Reveal key={`${f.title}-${i}`} delay={i * 0.05}>
                <div className="group h-full rounded-2xl border border-[#c9a35c22] bg-[#1a120e] p-6 transition-all hover:-translate-y-1 hover:border-[#c9a35c66] hover:bg-[#1e150f]" data-testid={`feature-${i}`}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl transition-colors" style={{ background: "linear-gradient(135deg,#c9a35c22,#e6c98a11)" }}>
                    <IconC className="h-5 w-5 text-[#e6c98a]" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-neutral-100" style={{ fontFamily: "'Inter', sans-serif" }}>{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-400" style={{ fontFamily: "'Inter', sans-serif" }}>{f.desc}</p>
                </div>
              </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="px-5 py-24" style={{ background: "linear-gradient(180deg,#0f0a08,#150e0a,#0f0a08)" }} data-testid="landing-benefits">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <p className="text-center text-xs uppercase tracking-[0.4em] text-[#c9a35c]" style={{ fontFamily: "'Inter', sans-serif" }}>Kenapa Wedifylab?</p>
            <h2 className="mt-3 text-center text-4xl italic sm:text-5xl" style={{ fontFamily: "'Great Vibes', cursive", color: "#f7efe4" }}>Manfaat untuk Anda</h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08}>
                <div className="rounded-2xl border border-[#c9a35c33] p-6 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{ background: "linear-gradient(135deg,#c9a35c,#e6c98a)" }}>
                    <Check className="h-6 w-6 text-[#0f0a08]" strokeWidth={3} />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold" style={{ fontFamily: "'Inter', sans-serif", color: "#f7efe4" }}>{b.title}</h3>
                  <p className="mt-2 text-sm text-neutral-400" style={{ fontFamily: "'Inter', sans-serif" }}>{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="px-5 py-24" data-testid="landing-testimonials">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <p className="text-center text-xs uppercase tracking-[0.4em] text-[#c9a35c]" style={{ fontFamily: "'Inter', sans-serif" }}>Testimonial</p>
              <h2 className="mt-3 text-center text-4xl italic sm:text-5xl" style={{ fontFamily: "'Great Vibes', cursive", color: "#f7efe4" }}>Kata mereka tentang Wedifylab</h2>
              <p className="mx-auto mt-4 max-w-2xl text-center text-neutral-400" style={{ fontFamily: "'Inter', sans-serif" }}>
                Cerita pasangan yang merencanakan hari bahagia mereka bersama kami.
              </p>
            </Reveal>
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {testimonials.map((t, i) => (
                <Reveal key={t.id} delay={i * 0.08}>
                  <div
                    data-testid={`testimonial-card-${t.id}`}
                    className="relative flex h-full flex-col rounded-2xl border border-[#c9a35c33] bg-[#150e0a]/60 p-6 backdrop-blur"
                  >
                    <div
                      className="pointer-events-none absolute -top-4 left-6 select-none text-6xl leading-none text-[#c9a35c]/40"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      &ldquo;
                    </div>
                    <p className="mt-4 flex-1 text-neutral-200" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: "1.05rem", lineHeight: "1.55" }}>
                      {t.quote}
                    </p>
                    <div className="mt-5 flex items-center gap-3 border-t border-[#c9a35c22] pt-4">
                      {t.photo ? (
                        <img
                          src={t.photo}
                          alt={t.couple_name}
                          className="h-12 w-12 rounded-full object-cover ring-2 ring-[#c9a35c]/50"
                        />
                      ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#c9a35c22] text-[#e6c98a]">
                          <Heart className="h-5 w-5 fill-current" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-semibold text-[#e6c98a]" style={{ fontFamily: "'Inter', sans-serif" }}>{t.couple_name}</p>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500" style={{ fontFamily: "'Inter', sans-serif" }}>Pasangan Wedifylab</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PLANS */}
      <section id="paket" className="px-5 py-24" data-testid="landing-plans">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <p className="text-center text-xs uppercase tracking-[0.4em] text-[#c9a35c]" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="landing-plans-eyebrow">{priceCms.eyebrow || "Pilihan Paket"}</p>
            <h2 className="mt-3 text-center text-4xl italic sm:text-5xl" style={{ fontFamily: "'Great Vibes', cursive", color: "#f7efe4" }} data-testid="landing-plans-heading">{priceCms.heading || "Pilih yang sesuai untuk Anda"}</h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-neutral-400" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="landing-plans-desc">
              {priceCms.description || "Semua paket termasuk update fitur, tema baru, dan dukungan tim Wedifylab."}
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {plansMerged.map((p, i) => {
              const isPopular = p.id === popularPlan;
              const scaleCls = isPopular ? "md:scale-[1.03]" : "";
              return (
              <Reveal key={p.id} delay={i * 0.1}>
                <div data-testid={`plan-${p.id}`} data-popular={isPopular ? "true" : "false"}
                  className={`relative flex h-full flex-col overflow-hidden rounded-3xl border p-8 transition-all hover:-translate-y-2 ${scaleCls} ${isPopular ? "border-2 border-[#c9a35c] bg-gradient-to-b from-[#1a120e] via-[#1e150f] to-[#1a120e] shadow-[0_0_50px_-15px_#c9a35c66]" : "border-[#c9a35c33] bg-[#1a120e]"}`}>
                  {isPopular && <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-[#e6c98a] to-transparent" />}
                  {isPopular && (
                    <Badge data-testid={`popular-badge-${p.id}`} className="self-start rounded-full border-0 bg-gradient-to-r from-[#c9a35c] to-[#e6c98a] px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-[#0f0a08]"
                      style={{ fontFamily: "'Inter', sans-serif" }}>
                      <Crown className="mr-1 h-3 w-3" /> Populer
                    </Badge>
                  )}
                  {!isPopular && p.badge && (
                    <Badge className="self-start rounded-full border border-[#c9a35c66] bg-[#c9a35c11] px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-[#e6c98a]"
                      style={{ fontFamily: "'Inter', sans-serif" }}>
                      {p.id === "premium" && <Crown className="mr-1 h-3 w-3" />}
                      {p.badge}
                    </Badge>
                  )}
                  <h3 className="mt-4 text-2xl font-semibold" style={{ fontFamily: "'Inter', sans-serif", color: "#f7efe4" }}>{p.name}</h3>
                  {p.short_desc && (
                    <p className="mt-1.5 text-sm italic text-neutral-400" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid={`plan-${p.id}-shortdesc`}>
                      {p.short_desc}
                    </p>
                  )}
                  <div className="mt-3 flex flex-col items-start gap-1">
                    {plansMeta[p.id]?.discount_active && plansMeta[p.id]?.original_price && (
                      <div className="flex items-center gap-2">
                        <span className="text-lg text-neutral-500 line-through" style={{ fontFamily: "'Inter', sans-serif" }} data-testid={`landing-original-${p.id}`}>
                          {formatRupiah(plansMeta[p.id].original_price)}
                        </span>
                        <Badge className="rounded-full border-0 bg-red-500/20 px-2 py-0 text-[9px] uppercase tracking-wider text-red-300" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {Math.round((1 - plansMeta[p.id].price / plansMeta[p.id].original_price) * 100)}% OFF
                        </Badge>
                      </div>
                    )}
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold" style={{ fontFamily: "'Inter', sans-serif", color: isPopular ? "#e6c98a" : "#f7efe4" }} data-testid={`landing-price-${p.id}`}>{formatRupiah(prices[p.id])}</span>
                      <span className="text-sm text-neutral-500" style={{ fontFamily: "'Inter', sans-serif" }}>{p.period}</span>
                    </div>
                    {plansMeta[p.id]?.discount_active && plansMeta[p.id]?.discount_type === "orders" && (
                      <p className="text-[10px] text-amber-400" style={{ fontFamily: "'Inter', sans-serif" }} data-testid={`landing-quota-${p.id}`}>
                        🔥 Sisa {plansMeta[p.id].discount_remaining_orders} kuota diskon
                      </p>
                    )}
                    {plansMeta[p.id]?.discount_active && plansMeta[p.id]?.discount_type === "time" && plansMeta[p.id]?.discount_end_at && (
                      <p className="text-[10px] text-amber-400" style={{ fontFamily: "'Inter', sans-serif" }} data-testid={`landing-until-${p.id}`}>
                        ⏰ Berakhir {new Date(plansMeta[p.id].discount_end_at).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    )}
                  </div>
                  <div className="my-6 h-px w-full bg-gradient-to-r from-transparent via-[#c9a35c44] to-transparent" />
                  <ul className="flex-1 space-y-3">
                    {p.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm text-neutral-300" style={{ fontFamily: "'Inter', sans-serif" }}>
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#e6c98a]" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button onClick={() => nav("/login")} data-testid={`plan-${p.id}-cta`}
                    className={`mt-8 w-full rounded-full py-6 text-sm font-semibold transition-transform hover:-translate-y-0.5 ${isPopular ? "text-[#0f0a08]" : "border border-[#c9a35c66] bg-transparent text-[#e6c98a] hover:bg-[#c9a35c11]"}`}
                    style={isPopular ? { background: "linear-gradient(90deg,#c9a35c,#e6c98a)", fontFamily: "'Inter', sans-serif" } : { fontFamily: "'Inter', sans-serif" }}>
                    {p.id === "premium" ? "Ambil Full Akses" : "Pilih Paket"}
                  </Button>
                </div>
              </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.3}>
            <p className="mt-8 text-center text-xs text-neutral-500" style={{ fontFamily: "'Inter', sans-serif" }}>
              Metode pembayaran yang didukung
            </p>
            <div className="mt-4">
              <PaymentLogos variant="dark" testid="landing-payment-logos" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="px-5 py-24">
        <div className="mx-auto max-w-3xl rounded-3xl border border-[#c9a35c44] p-10 text-center sm:p-14" style={{ background: "radial-gradient(circle at top, #c9a35c22, transparent 70%)" }}>
          <Reveal>
            <h2 className="text-4xl italic sm:text-6xl" style={{ fontFamily: "'Great Vibes', cursive", color: "#f7efe4" }} data-testid="landing-cta-title">{footerCms.cta_title || "Siap mulai?"}</h2>
            <p className="mt-6 text-neutral-300" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="landing-cta-desc">
              {footerCms.cta_desc || "Bergabung bersama pasangan lain yang menyusun pernikahan mereka lebih rapi dan indah bersama Wedifylab."}
            </p>
            <Button onClick={() => nav("/login")} size="lg" data-testid="landing-cta-final"
              className="mt-8 gap-2 rounded-full px-10 py-6 text-base font-semibold text-[#0f0a08] transition-transform hover:-translate-y-1"
              style={{ background: "linear-gradient(90deg,#c9a35c,#e6c98a)", fontFamily: "'Inter', sans-serif" }}>
              {footerCms.cta_button || "Masuk / Daftar Sekarang"} <ArrowRight className="h-4 w-4" />
            </Button>
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#c9a35c22] px-5 py-10" style={{ fontFamily: "'Inter', sans-serif" }}>
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-3 text-center text-xs text-neutral-500 sm:flex-row sm:justify-between">
          <p className="flex items-center gap-2" data-testid="landing-footer-copyright">
            <img src={LOGO} alt="" className="h-6 w-6 object-contain" />
            {footerCms.copyright_html ? (
              <span>{footerCms.copyright_html}</span>
            ) : (
              <span>© {new Date().getFullYear()} <b className="text-[#e6c98a]">Wedifylab</b> · Wedding Planner &amp; Digital Invitation</span>
            )}
          </p>
          <p className="flex items-center gap-1" data-testid="landing-footer-tagline">
            {footerCms.tagline ? (footerCms.tagline) : (<>build with <Heart className="h-3 w-3 fill-[#c9a35c] text-[#c9a35c]" /> untuk hari bahagia Anda</>)}
          </p>
        </div>
        {(footerCms.contact_email || footerCms.contact_phone) && (
          <div className="mx-auto mt-4 flex max-w-6xl flex-col items-center gap-2 text-center text-xs text-neutral-500 sm:flex-row sm:justify-center sm:gap-5" data-testid="landing-footer-contact">
            {footerCms.contact_email && <a href={`mailto:${footerCms.contact_email}`} className="hover:text-[#e6c98a]">{footerCms.contact_email}</a>}
            {footerCms.contact_phone && <span>{footerCms.contact_phone}</span>}
          </div>
        )}
      </footer>

      <WhatsAppFab position="right" context="informasi paket & fitur Wedifylab" />
    </div>
  );
}
